import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Navbar />
      
      <div className="pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-8 sm:p-12 shadow-sm border border-gray-100 dark:border-neutral-800">
            <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100 mb-8">Terms of Service</h1>
            <p className="text-gray-500 dark:text-neutral-500 mb-8">Last updated: February 4, 2026</p>
            
            <div className="prose prose-gray max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">1. Agreement to Terms</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  Welcome to Connectify eSIM. These Terms of Service ("Terms") govern your access to and use of our website, mobile applications, and services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms.
                </p>
                <p className="text-gray-600 dark:text-neutral-400">
                  If you do not agree to these Terms, please do not use our Services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">2. Description of Services</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  Connectify eSIM provides digital eSIM (embedded SIM) services that allow you to access mobile data networks while traveling internationally. Our Services include:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Purchase and activation of eSIM data plans</li>
                  <li>Management of eSIM profiles through our platform</li>
                  <li>Customer support for eSIM-related inquiries</li>
                  <li>Top-up services for existing eSIM plans</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">3. Account Registration</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">To use certain features of our Services, you must create an account. You agree to:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your information as needed</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized access</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  You may register using your email address or through third-party authentication services (Google, Apple). By using social login, you authorize us to access certain account information as permitted by those platforms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">4. eSIM Products and Services</h2>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">4.1 Product Descriptions</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  We strive to provide accurate descriptions of our eSIM plans, including coverage areas, data allowances, and validity periods. However, we do not warrant that product descriptions are error-free or complete.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">4.2 Device Compatibility</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  eSIM services require a compatible device. It is your responsibility to verify that your device supports eSIM technology before making a purchase. We provide a device compatibility checker, but cannot guarantee compatibility with all devices.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">4.3 Activation and Usage</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  eSIM profiles are typically delivered instantly after purchase. Activation requires scanning a QR code or using our one-tap install feature. Data plans begin upon first network connection in the destination country or as specified in the plan details.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">4.4 Network Coverage</h3>
                <p className="text-gray-600 dark:text-neutral-400">
                  Network coverage and speeds depend on local carrier infrastructure and are subject to availability. We partner with reputable network providers but cannot guarantee uninterrupted service in all locations.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">5. Pricing and Payment</h2>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">5.1 Pricing</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  All prices are displayed in your selected currency and include applicable taxes unless otherwise stated. We reserve the right to change prices at any time without prior notice.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">5.2 Payment Processing</h3>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  Payments are processed securely through our payment provider (Stripe). By making a purchase, you authorize us to charge your selected payment method for the total amount of your order.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 dark:text-neutral-200 mb-3">5.3 Currency Conversion</h3>
                <p className="text-gray-600 dark:text-neutral-400">
                  If your payment currency differs from the displayed currency, your bank may apply conversion fees. Exchange rates are determined by your payment provider.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">6. Refund Policy</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">Due to the digital nature of eSIM products:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li><strong>Before Activation:</strong> Full refunds may be available for unused eSIM profiles that have not been installed or activated</li>
                  <li><strong>After Activation:</strong> Refunds are generally not available once an eSIM has been activated or data has been consumed</li>
                  <li><strong>Technical Issues:</strong> If you experience technical issues preventing activation, please contact our support team for assistance and potential resolution</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  Refund requests should be submitted within 7 days of purchase. Each request is reviewed on a case-by-case basis.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">7. Acceptable Use</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">You agree not to:</p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Use the Services for any illegal or unauthorized purpose</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the Services</li>
                  <li>Resell or redistribute eSIM products without authorization</li>
                  <li>Use the Services for excessive data consumption that impacts network performance</li>
                  <li>Share eSIM credentials with unauthorized parties</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">8. Intellectual Property</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  All content, trademarks, logos, and intellectual property displayed on our Services are owned by Connectify eSIM or our licensors. You may not:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Copy, modify, or distribute our content without permission</li>
                  <li>Use our trademarks without written authorization</li>
                  <li>Remove any proprietary notices from our materials</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">9. Disclaimer of Warranties</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE</li>
                  <li>NON-INFRINGEMENT</li>
                  <li>UNINTERRUPTED OR ERROR-FREE SERVICE</li>
                  <li>ACCURACY OR RELIABILITY OF INFORMATION</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">10. Limitation of Liability</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, CONNECTIFY ESIM SHALL NOT BE LIABLE FOR:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Any indirect, incidental, special, or consequential damages</li>
                  <li>Loss of profits, data, or business opportunities</li>
                  <li>Network outages or service interruptions beyond our control</li>
                  <li>Issues arising from device incompatibility</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">11. Indemnification</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  You agree to indemnify, defend, and hold harmless Connectify eSIM and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Services or violation of these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">12. Termination</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  We may terminate or suspend your account and access to the Services at our sole discretion, without prior notice, for conduct that we believe:
                </p>
                <ul className="list-disc pl-6 text-gray-600 dark:text-neutral-400 space-y-2">
                  <li>Violates these Terms</li>
                  <li>Is harmful to other users or third parties</li>
                  <li>Is fraudulent or illegal</li>
                </ul>
                <p className="text-gray-600 dark:text-neutral-400 mt-4">
                  Upon termination, your right to use the Services will immediately cease. Unused data on active eSIM plans may not be refundable.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">13. Governing Law</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  These Terms shall be governed by and construed in accordance with the laws of Australia, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of Australia.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">14. Changes to Terms</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of the Services after changes constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">15. Severability</h2>
                <p className="text-gray-600 dark:text-neutral-400">
                  If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">16. Contact Information</h2>
                <p className="text-gray-600 dark:text-neutral-400 mb-4">
                  If you have any questions about these Terms, please contact us:
                </p>
                <ul className="list-none text-gray-600 dark:text-neutral-400 space-y-2">
                  <li><strong>Email:</strong> hello@connectify-esims.com</li>
                  <li><strong>Website:</strong> www.connectify-esims.com</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
